import React, { useState } from 'react';
import { FileTextIcon, BoxIcon } from 'lucide-react';
type Status = 'red' | 'yellow' | 'green';
interface StatusLabels {
  red?: string;
  yellow?: string;
  green?: string;
}
const STATUS_STYLES: Record<Status, {
  card: string;
  text: string;
  dot: string;
  ring: string;
}> = {
  red: {
    card: 'bg-red-50 border-red-200',
    text: 'text-red-700',
    dot: 'bg-red-400',
    ring: 'ring-red-500'
  },
  yellow: {
    card: 'bg-yellow-50 border-yellow-200',
    text: 'text-yellow-700',
    dot: 'bg-yellow-400',
    ring: 'ring-yellow-500'
  },
  green: {
    card: 'bg-green-50 border-green-200',
    text: 'text-green-700',
    dot: 'bg-green-400',
    ring: 'ring-green-500'
  }
};
const DEFAULT_LABELS: Required<StatusLabels> = {
  red: 'Not Started',
  yellow: 'Ordered',
  green: 'Received'
};
interface StatusCardProps {
  title: string;
  icon?: BoxIcon;
  initialStatus?: Status;
  /** Optional per-state status labels (e.g. { yellow: 'Disclosed', green: 'Signed' }). */
  statusLabels?: StatusLabels;
  /** Labels for the date stamps shown beneath the card. Defaults to status labels. */
  dateLabels?: StatusLabels;
}
function formatDate(date: Date) {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}
export function StatusCard({
  title,
  icon: Icon = FileTextIcon,
  initialStatus = 'red',
  statusLabels,
  dateLabels
}: StatusCardProps) {
  const labels = {
    ...DEFAULT_LABELS,
    ...statusLabels
  };
  const stampLabels = {
    ...labels,
    ...dateLabels
  };
  const [status, setStatus] = useState<Status>(initialStatus);
  const [yellowDate, setYellowDate] = useState<string | null>(null);
  const [greenDate, setGreenDate] = useState<string | null>(null);
  const handleChange = (next: Status) => {
    if (next === status) return;
    const today = formatDate(new Date());
    if (next === 'yellow' && !yellowDate) {
      setYellowDate(today);
    }
    if (next === 'green') {
      if (!yellowDate) setYellowDate(today);
      if (!greenDate) setGreenDate(today);
    }
    if (next === 'red') {
      setYellowDate(null);
      setGreenDate(null);
    }
    setStatus(next);
  };
  const style = STATUS_STYLES[status];
  const showYellow = status === 'yellow' || status === 'green';
  const showGreen = status === 'green';
  return <div className="flex-1 min-w-0" data-id="element-29">
      <div className={`rounded-md border ${style.card} p-2 transition-colors`} data-id="element-30">
        <div className="flex items-center justify-between gap-1" data-id="element-31">
          <div className="flex items-center gap-1 text-slate-600 min-w-0" data-id="element-32">
            <Icon className="h-3 w-3 shrink-0" aria-hidden="true" data-id="element-33" />
            <span className="text-[10px] font-semibold tracking-wider uppercase truncate" data-id="element-34">
              {title}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0" role="radiogroup" aria-label={`${title} status`} data-id="element-35">
            {(['red', 'yellow', 'green'] as Status[]).map(s => {
            const selected = status === s;
            const sStyle = STATUS_STYLES[s];
            return <button key={s} type="button" role="radio" aria-checked={selected} aria-label={labels[s]} onClick={() => handleChange(s)} className={`h-2.5 w-2.5 rounded-full ${sStyle.dot} transition-all ${selected ? `ring-1 ring-offset-1 ${sStyle.ring}` : 'opacity-50 hover:opacity-100'}`} data-id="element-36" />;
          })}
          </div>
        </div>
        <div className={`mt-1.5 text-center text-xs font-medium ${style.text} truncate`} data-id="element-37">
          {labels[status]}
        </div>
      </div>

      {(showYellow || showGreen) && <div className="mt-1 rounded-md bg-white border border-slate-200 px-2 py-1 text-xs" data-id="element-38">
          {showYellow && <div className="flex items-center justify-between gap-1" data-id="element-39">
              <span className="text-[9px] font-medium uppercase tracking-wider text-slate-500 truncate" data-id="element-40">
                {stampLabels.yellow}
              </span>
              <span className="text-slate-700 text-[11px] shrink-0" data-id="element-41">
                {yellowDate}
              </span>
            </div>}
          {showGreen && <div className="flex items-center justify-between gap-1" data-id="element-42">
              <span className="text-[9px] font-medium uppercase tracking-wider text-slate-500 truncate" data-id="element-43">
                {stampLabels.green}
              </span>
              <span className="text-slate-700 text-[11px] shrink-0" data-id="element-44">
                {greenDate}
              </span>
            </div>}
        </div>}
    </div>;
}